import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "content-grid content-grid--subpage content-grid--subpage-table" }
const _hoisted_2 = { class: "content-grid__header" }
const _hoisted_3 = { class: "back-link-title" }
const _hoisted_4 = { class: "content-grid__filters" }

import Card from "@/components/layout/Card.vue";
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import SearchInput from "@/components/layout/SearchInput.vue";
import {useI18n} from "vue3-i18n";
import {computed, onMounted, ref, watch} from "vue";
import {useActivityOccurrenceService} from "@/inversify.config";
import DataTable from "@/components/layout/DataTable.vue";
import BtnLink from "@/components/layout/BtnLink.vue";
import {Guid} from "@/types";
import {notifyError, notifySuccess} from "@/notify";
import {SucceededOrNotResponse} from "@/types/responses/succeededOrNotResponse";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";


export default /*#__PURE__*/_defineComponent({
  __name: 'AdminActivityOccurrenceIndex',
  setup(__props) {

const {t} = useI18n()

const activityOccurrenceService = useActivityOccurrenceService();

const searchValue = ref("");
const showPastActivities = ref(false);
const activityOccurrencesAreLoading = ref(false);
const activityOccurrences = ref<ActivityOccurrence[]>([]);

watch(showPastActivities, async (newValue) => {
  await loadFutureOrPastOccurrences(newValue)
})

onMounted(async () => {
  await loadFutureOrPastOccurrences(showPastActivities.value);
})

const activityOccurrenceItems = computed(() => {
  let items: any[] = [];
  if (activityOccurrences.value.length === 0)
    return []

  activityOccurrences.value.forEach((activityOccurrence: ActivityOccurrence) => {
    const occupiedPlaces = activityOccurrence.occupiedPlaces != null ? activityOccurrence.occupiedPlaces : 0
    items.push({
      id: activityOccurrence.id,
      name: activityOccurrence.activity?.nameFr?.truncateString(42),
      startDate: activityOccurrence.startDate ? activityOccurrence.startDate : t("global.undefined"),
      endDate: activityOccurrence.endDate ? activityOccurrence.endDate : t("global.undefined"),
      participants: `${occupiedPlaces}/${activityOccurrence.activity.maxParticipants}`,
      isPublished: activityOccurrence.activity?.isPublished ? t("global.yes") : t("global.no"),
      actions: {
        update: {name: "admin.children.activities.update", params: {id: activityOccurrence.activity?.id}},
        participants: {
          name: "admin.children.activities.participants.edit",
          params: {activityId: activityOccurrence.activity?.id, activityOccurrenceId: activityOccurrence.id}
        },
        registrations: {
          name: "admin.children.activities.registrations.edit",
          params: {activityId: activityOccurrence.activity?.id}
        },
        delete: true,
      }
    });
  });

  return items;
});

async function loadFutureOrPastOccurrences(showPastActivities: boolean) {
  activityOccurrencesAreLoading.value = true;
  let response = showPastActivities
      ? await activityOccurrenceService.getPastActivityOccurrences()
      : await activityOccurrenceService.getFutureActivityOccurrences();
  if (response.length > 0) {
    activityOccurrences.value = response;
  }
  activityOccurrencesAreLoading.value = false;
}

async function deleteActivityOccurrence(activityOccurrence: any) {
  if (activityOccurrence?.id == null || !Guid.isValid(activityOccurrence.id ?? ""))
    return

  let confirmDelete = confirm(t("activityOccurrences.form.deleteConfirmation"));
  if (!confirmDelete)
    return;

  let result: SucceededOrNotResponse = await activityOccurrenceService.deleteActivityOccurrence(activityOccurrence.id);
  if (result?.succeeded) {
    notifySuccess(t('activityOccurrences.form.deleteSuccess'))
    await loadFutureOrPastOccurrences(showPastActivities.value);
  } else {
    notifyError(t('activityOccurrences.form.deleteError'))
  }
}

const activityOccurrencesHeader = [
  {
    text: t("products.form.name"),
    value: "name",
    sortable: true,
    width: 315,
  },
  {
    text: t("activities.form.startDate"),
    value: "startDate",
    sortable: true,
    width: 135,
  },
  {
    text: t("activities.form.endDate"),
    value: "endDate",
    sortable: true,
    width: 125,
  },
  {
    text: t("activities.form.fieldset-participants"),
    value: "participants",
    width: 80,
  },
  {
    text: t("activities.form.isPublished"),
    value: "isPublished",
    width: 105,
  },
  {
    text: t("global.table.actions"),
    value: "actions",
    width: 175
  },
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Breadcrumbs, {
      title: _unref(t)(`routes.admin.children.activities.name`)
    }, null, 8, ["title"]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)(`routes.admin.children.activities.name`)), 1),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(SearchInput, {
          modelValue: searchValue.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchValue).value = $event))
        }, null, 8, ["modelValue"]),
        _createVNode(FormCheckbox, {
          modelValue: showPastActivities.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showPastActivities).value = $event)),
          label: _unref(t)('activities.showPastActivities'),
          class: "form",
          name: "show-past-activities"
        }, null, 8, ["modelValue", "label"])
      ])
    ]),
    _createVNode(Card, {
      title: _unref(t)('global.quickLinks'),
      class: "card--row-content"
    }, {
      default: _withCtx(() => [
        _createVNode(BtnLink, {
          name: _unref(t)('routes.admin.children.activities.add.name'),
          path: {name: `admin.children.activities.add`}
        }, null, 8, ["name", "path"]),
        _createVNode(BtnLink, {
          name: _unref(t)(`routes.admin.children.activities.participants.add.name`),
          path: {name: `admin.children.activities.participants.add`}
        }, null, 8, ["name", "path"]),
        _createVNode(BtnLink, {
          name: _unref(t)(`routes.admin.children.activities.registrations.add.name`),
          path: {name: `admin.children.activities.registrations.add`}
        }, null, 8, ["name", "path"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Card, null, {
      default: _withCtx(() => [
        _createVNode(DataTable, {
          headers: activityOccurrencesHeader,
          "is-loading": activityOccurrencesAreLoading.value,
          items: activityOccurrenceItems.value,
          "search-value": searchValue.value,
          onDelete: deleteActivityOccurrence
        }, null, 8, ["is-loading", "items", "search-value"])
      ]),
      _: 1
    })
  ]))
}
}

})